<template>
  <div id="__UsageReportMainView" ref="main-view">
    <HeadContents :loading="loading" @click-action="onClickAction" />

    <CTabs
      v-model="tab"
      :tab-items="tabItems"
      dense
      class="pl-xs pr-xs"
      v-if="tabItems.length > 1"
    />

    <CTabPanels v-model="tab">
      <CTabPanel
        name="cost"
        no-padding
        v-if="$display('cost_forecast_tab') && groupBy !== 'report_filter'"
      >
        <CPanel :loading="loading">
          <CostForecast ref="cost_forecast" :loading="loading" :id="id" :type="groupBy" />
        </CPanel>
      </CTabPanel>

      <CTabPanel name="analyze" no-padding>
        <CPanel :loading="loading">
          <Analyze ref="analyze" />
        </CPanel>
      </CTabPanel>

      <CTabPanel name="reports" no-padding>
        <CPanel :loading="loading">
          <BillingReport :owner="owner" :owner-id="ownerId" :type="reportCostType" />
        </CPanel>
      </CTabPanel>

      <CTabPanel name="detail" no-padding>
        <CPanel :loading="loading">
          <Detail />
        </CPanel>
      </CTabPanel>
    </CTabPanels>

    <!-- Dialog -->
    <DialogEditAccount v-model="dialogEditAccount" />
    <DialogExtraInfo v-model="dialogExtraInfo" />
    <DialogAlertSetting v-model="dialogAlertSetting" />
    <!--    <DialogEditGroup v-model="dialogEditGroup" @success="fetchDetail({ force: true })" />-->
    <DialogEditGroup v-model="dialogEditGroup" @success="onSuccessEditGroup" />
    <DialogDeleteGroup v-model="dialogDeleteGroup" @success="onSuccessDeleteGroup" />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapMutations, mapState, mapGetters } from 'vuex';
import heroChartMixin from '@/components/CustomCharts/heroChartMixin';
import { disposeAllCharts } from '@/components/CustomCharts/chartFn';

import CPanel from '@/components/uikit/CPanel';
import CTabs from '@/components/uikit/CTabs';
import CTabPanels from '@/components/uikit/CTabs/CTabPanels';
import CTabPanel from '@/components/uikit/CTabs/CTabPanel';

// Contents
import HeadContents from '@/pages/UsageReport/HeadContents';
import CostForecast from '@/pages/UsageReport/CostForecast';
import Analyze from '@/pages/UsageReport/Analyze';
import BillingReport from '@/components/BillingReport/BillingReport';
import Detail from '@/pages/UsageReport/Detail';

// Dialog
import DialogEditAccount from '@/pages/UsageReport/DialogEditAccount';
import DialogExtraInfo from '@/pages/UsageReport/DialogExtraInfo';
import DialogAlertSetting from '@/pages/UsageReport/DialogAlertSetting';
import DialogEditGroup from '@/pages/UsageReport/DialogEditGroup';
import DialogDeleteGroup from '@/pages/UsageReport/DialogDeleteGroup';

import { analyzer } from '@/analyzer';

const messages = {
  en: {
    Cost: 'Cost',
    Analyze: 'Analytics',
    Report: 'Reports',
    Detail: 'Details',
  },
  ja: {
    Cost: 'コスト',
    Analyze: '分析',
    Report: 'レポート',
    Detail: '情報',
  },
};

export default Vue.extend({
  name: 'MainView',
  mixins: [heroChartMixin],
  i18n: { messages },
  components: {
    CTabs,
    CTabPanels,
    CTabPanel,
    CPanel,
    HeadContents,
    CostForecast,
    Analyze,
    BillingReport,
    Detail,
    DialogEditAccount,
    DialogExtraInfo,
    DialogAlertSetting,
    DialogEditGroup,
    DialogDeleteGroup,
  },
  data() {
    return {
      tab: this.$display('cost_forecast_tab') ? 'cost' : 'analyze',
      tabItems: [],
      chart: {
        forecast: false,
        hero: false,
      },

      // Dialog
      dialogEditAccount: false,
      dialogExtraInfo: false,
      dialogAlertSetting: false,
      dialogEditGroup: false,
      dialogDeleteGroup: false,
    };
  },
  watch: {
    tab() {
      this.updateContents();
    },

    async id() {
      this.updateLogtime(Date.now());
      analyzer.track(`Perform: UsageReportCreatedTab`, {
        groupBy: this.groupBy,
        id: this.id,
        tab: this.tab,
        logtime2: Number(Date.now()),
      });

      this.tabItems = this.createTabItems();

      // Check if current tab exists in new id's tab items
      var hasCurrentTabItem = false;
      for (const item of this.tabItems) {
        if (item.value === this.tab) {
          hasCurrentTabItem = true;
          break;
        }
      }
      if (!hasCurrentTabItem) {
        // Reset tab
        this.tab = this.tabItems[0].value;
      }

      // Reset chart state
      this.chart.forecast = false;
      this.chart.hero = false;

      // Scroll to top
      let element = document.getElementById('__UsageReportMainView');
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Set conditions - Analyze chart
      await this.setInitialChartConditions();
      // Fetch detail
      await this.fetchDetail({
        force: false,
        fetchCostForecast: this.$display('cost_forecast_tab'),
      });
      // await this.fetchDetail({ force: false });
      // Update contents - Charts
      await this.updateContents();
    },
  },
  async created() {
    this.tabItems = this.createTabItems();
    this.tab = this.tabItems[0].value; //set default tab

    if (this.id) {
      analyzer.track(`Perform: UsageReportCreated`, {
        groupBy: this.groupBy,
        id: this.id,
        tab: this.tab,
        logtime2: Number(Date.now()),
      });

      // Set conditions - Analyze chart
      await this.setInitialChartConditions();

      // Fetch detail
      // await this.fetchDetail({ force: false });
      await this.fetchDetail({
        force: false,
        fetchCostForecast: this.$display('cost_forecast_tab'),
      });
      // TODO: Temporary fix. これがないと、tab = costの場合にチャートが作成されない。
      if (this.tab === 'cost' || this.tab === 'analyze') {
        await this.updateContents();
      }

      if (this.vendor === 'aws') {
        /**somtimes the chart data is empty array, if so reload the data  */
        this.$nextTick(() => {
          if (
            this.$refs.cost_forecast &&
            this.$refs.cost_forecast.$refs['forecast-monthly-chart']
          ) {
            if (!this.$refs.cost_forecast.$refs['forecast-monthly-chart'].chart._data.length) {
              this.reload();
            }
          }
        });
      }
    }

    // Update tab
    this.tab = this.tabItems[0].value;
  },
  beforeDestroy() {
    disposeAllCharts();
    this.tab = this.tabItems[0].value;
  },
  computed: {
    ...mapState({
      vendor: state => state.vendor,
    }),

    ...mapState('usageReports', {
      groupBy: state => state.groupBy,
      id: state => state.id,
      loading: state => state.loading.fetchDetail,
    }),

    ...mapGetters('usageReports', {
      owner: 'getOwner',
      ownerId: 'getOwnerId',
      logtimeInterval: 'getLogtimeInterval',
    }),

    ...mapGetters('user', {
      reportCostType: 'getReportCostType',
    }),
  },
  methods: {
    ...mapActions('usageReports', ['fetchList', 'fetchDetail', 'fetchAnalyzeData']),
    ...mapMutations('usageReports', ['updateGroupBy', 'updateLogtime']),

    createTabItems() {
      const items = [];
      // Per Vendor
      if (
        this.vendor === 'aws' &&
        this.groupBy !== 'cross_tag' &&
        this.groupBy !== 'report_filter' &&
        this.$display('cost_forecast_tab')
      ) {
        items.splice(0, 0, {
          label: this.$t('Cost'),
          value: 'cost',
        });
      }

      // Per Group by
      switch (this.groupBy) {
        case 'account':
          // Analyze graph
          if (this.$display('usage_account_graph')) {
            items.splice(1, 0, {
              label: this.$t('Analyze'),
              value: 'analyze',
              disable: this.loading,
            });
          }

          // Report
          items.splice(2, 0, {
            label: this.$t('Report'),
            value: 'reports',
            disable: this.loading,
          });

          this.groupBy = 'account';
          break;

        case 'azure_subscription':
          // Analyze graph
          if (this.$display('usage_account_graph')) {
            items.splice(1, 0, {
              label: this.$t('Analyze'),
              value: 'analyze',
              disable: this.loading,
            });
          }

          // Report
          items.splice(2, 0, {
            label: this.$t('Report'),
            value: 'reports',
            disable: this.loading,
          });
          break;

        case 'acc_group':
          // Analyze graph
          if (this.$display('usage_group_graph')) {
            items.splice(1, 0, {
              label: this.$t('Analyze'),
              value: 'analyze',
              disable: this.loading,
            });
          }

          // Report
          items.splice(2, 0, {
            label: this.$t('Report'),
            value: 'reports',
            disable: this.loading,
          });
          break;

        case 'cross_tag':
          // Analyze graph
          if (this.id === 'nontag') {
            // Report
            items.splice(0, 0, {
              label: this.$t('Report'),
              value: 'reports',
              disable: this.loading,
            });
          } else {
            if (this.$display('usage_crosstag_graph')) {
              items.splice(0, 0, {
                label: this.$t('Analyze'),
                value: 'analyze',
                disable: this.loading,
              });
            }
            // Report
            items.splice(1, 0, {
              label: this.$t('Report'),
              value: 'reports',
              disable: this.loading,
            });
          }
          this.groupBy = 'cross_tag';

          break;

        case 'report_filter':
          // Analyze graph
          if (this.$display('usage_account_graph')) {
            items.splice(1, 0, {
              label: this.$t('Analyze'),
              value: 'analyze',
              disable: this.loading,
            });
          }

          // Report
          items.splice(2, 0, {
            label: this.$t('Report'),
            value: 'reports',
            disable: this.loading,
          });
          break;
      }

      if (
        (this.groupBy === 'cross_tag' && this.id === 'nontag') ||
        this.groupBy === 'report_filter'
      ) {
        // Don't show 'Detail' tab for nontag
      } else {
        items.push({
          label: this.$t('Detail'),
          value: 'detail',
          disable: this.loading,
        });
      }

      return items;
    },

    async updateContents() {
      /**
       * Cost tab
       */
      if (this.tab === 'cost') {
        if (!this.chart.forecast) {
          // Set data to charts - Forecast
          this.setChartData();
        }
      }

      /**
       * Analyze tab
       */
      if (this.tab === 'analyze') {
        // if (!this.chart.hero) {
        this.$nextTick(() => {
          // Set series - Analyze chart
          this.$refs['analyze'].setSeries();

          // Generate chart - Analyze chart
          this.$refs['analyze'].generateChart().then(res => {
            this.chart[res] = true;
          });
        });
        // }
      }
      analyzer.track(`Action: UsageReport ${this.groupBy} ${this.id} show ${this.tab} `);
      analyzer.track(`Perform: UsageReportComplete`, {
        groupBy: this.groupBy,
        id: this.id,
        tab: this.tab,
        logtime2: Number(Date.now()),
        interval2: Number(this.logtimeInterval),
      });
    },

    async reload() {
      this.tab = this.tabItems[0].value;

      // Reset chart state
      this.chart.forecast = false;
      this.chart.hero = false;

      // Fetch data - Forecast
      // await this.fetchDetail({ force: true });
      await this.fetchDetail({
        force: true,
        fetchCostForecast: this.$display('cost_forecast_tab'),
      });

      // Set data to charts - Forecast
      this.setChartData();
    },

    setChartData() {
      if (this.$refs['cost_forecast']) this.$refs['cost_forecast'].setChartData();
    },

    onClickAction(action) {
      analyzer.track(`Action: UsageReport ${this.id} ${action}`);
      switch (action) {
        case 'edit-account':
          this.dialogEditAccount = true;
          break;

        case 'extra-info':
          this.dialogExtraInfo = true;
          break;

        case 'alert-setting':
          this.dialogAlertSetting = true;
          break;

        case 'budget-setting':
          this.$router.push(`${this.id}/budget`).catch(() => {});
          break;

        // Acc Group
        case 'edit-group':
          this.dialogEditGroup = true;
          break;

        case 'delete-group':
          this.dialogDeleteGroup = true;
          break;

        // Common
        case 'reload':
          this.reload();
          break;
      }
    },

    onSuccessDeleteGroup() {
      analyzer.track(`Action: UsageReport deleteGroup`);
      this.fetchList({ force: true });
    },

    async onSuccessEditGroup() {
      await this.fetchDetail({
        force: true,
        fetchCostForecast: this.$display('cost_forecast_tab'),
      });
    },
  },
});
</script>
